<template>
  <div id="account-selection">
    <div
      class="rounded-md border border-outline bg-surface h-control flex items-center transition-colors select-none hover:border-brand hover:cursor-pointer text-secondary"
      :class="sidebarToggled ? 'px-2.5 justify-between' : 'justify-center'"
      @click="handleClick"
    >
      <template v-if="sidebarToggled">
        <span class="font-medium text-base truncate mr-2 flex items-center">
          <template v-if="sessionIcon">
            <div
              class="w-6 h-6 rounded-full flex items-center justify-center mr-2 shrink-0"
            >
              <SvgIcon :icon="sessionIcon" class="text-brand" />
            </div>
          </template>
          <Avatar v-else :entity="avatarEntity" />
          {{ displayName }}
        </span>
        <SvgIcon
          icon="ArrowDown"
          class="shrink-0 w-2.5 h-2.5 mr-0.5"
          :class="{ 'transform rotate-180 text-brand': show }"
        />
      </template>
      <template v-else>
        <div
          v-if="sessionIcon"
          class="w-6 h-6 rounded-full flex items-center justify-center"
        >
          <SvgIcon :icon="sessionIcon" class="text-brand" />
        </div>
        <Avatar v-else :entity="avatarEntity" class="mx-0" />
      </template>
    </div>

    <Menu v-model="show" :max-width="250" max-height="auto">
      <SessionSelector
        :current-session="currentSession"
        @select="handleSessionSelect"
      />
    </Menu>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { mapState } from 'pinia'

import { useGlobalStore } from '@/stores/global'
import { useUserStore } from '@/stores/user'
import { getTextFromRootUser } from '@/helpers/entity'

import Avatar from '@/components/misc/Avatar'
import Menu from '@/components/misc/Menu'
import SvgIcon from '@/components/misc/SvgIcon'
import SessionSelector from './SessionSelector.vue'

import type { AvatarEntity } from '@/types/components/avatar'
import type { UserSession } from '@/types/services/user'

export default defineComponent({
  components: {
    Avatar,
    Menu,
    SessionSelector,
    SvgIcon,
  },

  data() {
    return {
      show: false,

      globalStore: useGlobalStore(),
      userStore: useUserStore(),
    }
  },

  computed: {
    ...mapState(useGlobalStore, ['sidebarToggled']),
    ...mapState(useUserStore, [
      'currentView',
      'entity',
      'root',
      'activeAssistantClient',
    ]),

    displayName(): string {
      if (this.currentView === 'client') {
        return this.$t('ui.auth.client_view')
      }
      if (this.currentView === 'referral') {
        return this.$t('ui.auth.referral_view')
      }
      if (this.currentView === 'assistant' && this.activeAssistantClient) {
        return `${getTextFromRootUser(this.activeAssistantClient.root_user)} - ${this.activeAssistantClient.entity_name}`
      }
      return this.entity?.name ?? ''
    },

    sessionIcon(): string | null {
      if (this.currentView === 'client') {
        return 'Users'
      }
      if (this.currentView === 'referral') {
        return 'UserCoins'
      }
      if (this.currentView === 'assistant') {
        return 'UserCog'
      }
      return null
    },

    avatarEntity(): AvatarEntity | null {
      if (!this.root) {
        return null
      }
      return {
        id: this.root.id,
        text: this.root.name,
      }
    },

    currentSession(): UserSession | null {
      if (!this.currentView) {
        return null
      }

      return {
        role: this.currentView,
        entity_id: this.entity?.id,
      }
    },
  },

  methods: {
    handleClick() {
      if (this.sidebarToggled) {
        this.show = !this.show
      } else {
        this.globalStore.toggleSidebar()
      }
    },

    handleSessionSelect(session: UserSession) {
      this.userStore.changeSession(session)
      this.show = false
    },
  },
})
</script>
